import type { Pose } from "../core/types.ts";

/**
 * Tracks where "straight ahead" actually is for this person, right now.
 *
 * An arrow driven by raw pose points permanently down. Measured across six real
 * phone sessions, pitch sat between -8° and -28° in every single one and yaw was
 * consistently asymmetric — because a phone is held below eye level and looked
 * down at. Absolute zero is not a posture anyone adopts.
 *
 * So neutral is learned. A slow exponential mean converges on whatever the
 * person's resting posture is and stays there, while deliberate movement — which
 * is fast and returns — passes straight through. The same asymmetry that makes
 * this necessary is what makes it work: you cannot hold a turn indefinitely, so
 * anything sustained is posture, not intent.
 */
export class PoseNeutral {
  #neutral: Pose | null = null;

  /**
   * @param rate per-sample weight. Small enough that a 2-second gesture barely
   *   moves it, large enough to settle within a few seconds of starting.
   */
  constructor(private readonly rate = 0.004) {}

  get value(): Pose {
    return this.#neutral ?? { pitch: 0, yaw: 0, roll: 0 };
  }

  get settled(): boolean {
    return this.#neutral !== null;
  }

  /** @returns deflection from neutral, radians. */
  push(pose: Pose): Pose {
    if (!this.#neutral) {
      this.#neutral = { ...pose };
      return { pitch: 0, yaw: 0, roll: 0 };
    }
    const n = this.#neutral;
    n.pitch += (pose.pitch - n.pitch) * this.rate;
    n.yaw += (pose.yaw - n.yaw) * this.rate;
    n.roll += (pose.roll - n.roll) * this.rate;

    return {
      pitch: pose.pitch - n.pitch,
      yaw: pose.yaw - n.yaw,
      roll: pose.roll - n.roll,
    };
  }

  reset(): void {
    this.#neutral = null;
  }
}

/** Deflection beyond which the arrow reads as fully deflected. ~12°. */
const FULL_DEFLECTION = 0.21;

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/**
 * Draws the steering channel: a direction the player is pointing their head.
 *
 * This is a readout, not decoration. Design doc §3 recommends head pose as the
 * secondary input precisely because it is free — the transformation matrix is
 * already being computed — and the recorded sessions back that up with 50–120x
 * signal-to-noise and 14–28° of usable yaw. Drawing it is how we find out
 * whether it is usable *as a control* before building anything on top.
 *
 * §3 also warns against gaze direction for this job: aiming with the eyes is
 * fatiguing and works against a product whose point is eye comfort.
 */
export class PoseArrow {
  readonly #ctx: CanvasRenderingContext2D;

  constructor(private readonly canvas: HTMLCanvasElement) {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("2D canvas context unavailable");
    this.#ctx = ctx;
  }

  resize(width: number, height: number): void {
    if (this.canvas.width === width && this.canvas.height === height) return;
    this.canvas.width = width;
    this.canvas.height = height;
  }

  clear(): void {
    this.#ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  /** @param deflection pose relative to neutral, from PoseNeutral.push. */
  draw(deflection: Pose, settled: boolean): void {
    const ctx = this.#ctx;
    const { width: w, height: h } = this.canvas;
    ctx.clearRect(0, 0, w, h);

    // Yaw is negated: turning your head left should point the arrow left on a
    // mirrored preview, which is the only reading that feels correct.
    const x = clamp(-deflection.yaw / FULL_DEFLECTION, -1, 1);
    const y = clamp(-deflection.pitch / FULL_DEFLECTION, -1, 1);
    const magnitude = Math.min(1, Math.hypot(x, y));

    const cx = w / 2;
    const cy = h / 2;
    const radius = Math.min(w, h) * 0.3;

    ctx.save();
    ctx.translate(cx, cy);

    // Dead-zone ring: shows where neutral is, so a drifting baseline is visible
    // rather than something the player silently fights.
    ctx.strokeStyle = "rgba(125,135,146,0.35)";
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 4]);
    ctx.beginPath();
    ctx.arc(0, 0, radius * 0.18, 0, Math.PI * 2);
    ctx.stroke();
    ctx.setLineDash([]);

    if (!settled || magnitude < 0.12) {
      ctx.restore();
      return;
    }

    const angle = Math.atan2(y, x);
    const length = radius * (0.25 + magnitude * 0.75);
    ctx.rotate(angle);

    const hue = 140 - magnitude * 40;
    ctx.strokeStyle = `hsl(${hue} 70% 60% / ${0.35 + magnitude * 0.5})`;
    ctx.fillStyle = ctx.strokeStyle;
    ctx.lineWidth = 3;
    ctx.lineCap = "round";

    ctx.beginPath();
    ctx.moveTo(radius * 0.18, 0);
    ctx.lineTo(length, 0);
    ctx.stroke();

    const head = 9 + magnitude * 5;
    ctx.beginPath();
    ctx.moveTo(length + head, 0);
    ctx.lineTo(length - head * 0.4, head * 0.6);
    ctx.lineTo(length - head * 0.4, -head * 0.6);
    ctx.closePath();
    ctx.fill();

    ctx.restore();
  }
}
